/**
 * Marketing / landing-page i18n.
 *
 * Kept separate from the viewer i18n so the landing page can ship without
 * pulling in every viewer label. Copy is a faithful adaptation of the
 * customer-approved English / Arabic / Turkish drafts.
 *
 * Usage:
 *   const t = LANDING_I18N[lang] ?? LANDING_I18N.en;
 *   t.hero.headline
 */
export const LANDING_LANGS = [
  { code: "en", label: "English",  short: "EN", rtl: false },
  { code: "ar", label: "العربية",  short: "AR", rtl: true  },
  { code: "tr", label: "Türkçe",   short: "TR", rtl: false },
];

const en = {
  nav:  { explore: "Explore demo", signIn: "Sign in", bookDemo: "Book a demo" },
  hero: {
    eyebrow: "Interactive Real Estate Sales Platform",
    headline: "Sell real estate projects through an immersive digital experience.",
    sub: "Let buyers explore your project, discover available units, compare apartments, view plans and 360 tours, receive branded offers, and contact your sales team — all from one premium interactive platform.",
    primary: "Book a demo", secondary: "Explore live demo",
    ipad: "Showroom-ready · iPad · Desktop · Mobile",
  },
  problem: {
    eyebrow: "Why it matters",
    title: "Static websites show information. Interactive experiences create decisions.",
    sub: "Your buyers do not want to scroll through endless brochures. They want to understand the project, compare the right units, see availability, review payment options, and feel confident before speaking to your sales team.",
    cards: [
      { t: "Brochures go stale in weeks", d: "Prices, availability and inventory drift out of sync the moment a PDF is printed." },
      { t: "Sales teams repeat themselves", d: "The same availability, price and view questions consume hours every week." },
      { t: "Buyers cannot compare units",   d: "Without a smart interface, choosing between 40 apartments feels overwhelming." },
      { t: "Hot leads slip through",        d: "Interest is lost when the follow-up is generic and can't reference what the buyer explored." },
    ],
  },
  immersive: {
    eyebrow: "Immersive experience",
    title: "Make buyers feel the project before they visit it.",
    sub: "Guide buyers through your project — from the overall masterplan to individual blocks, floors, and apartments. Explore unit details, plans, views, galleries, and 360 tours in a smooth showroom-ready experience.",
    bullets: ["Masterplan navigation", "Block & floor drill-down", "Live availability", "360 tours & galleries", "Payment plans", "Contact in one tap"],
  },
  smart: {
    eyebrow: "Smart unit selection",
    title: "Help buyers find the right unit faster.",
    sub: "Filter by apartment type, price range, area, view, floor, status and custom project fields. Buyers and sales teams can narrow hundreds of units into a focused shortlist in seconds.",
    chips: ["1+1 / 2+1 / 3+1", "Sea view", "Under $150,000", "70–110 m²", "Available only", "Floor 5+"],
  },
  sales: {
    eyebrow: "Sales tools",
    title: "Built for real estate sales teams.",
    sub: "Everything your team needs to move from browse to signed offer — inside one experience.",
    items: [
      { t: "Shortlist units",        d: "Save favourites during a showroom visit or client demo." },
      { t: "Compare apartments",     d: "Side-by-side plan, view, price and area comparison." },
      { t: "Branded PDF offers",     d: "Generate a beautiful, developer-branded offer in one tap." },
      { t: "Share direct unit links",d: "Buyer opens the exact apartment you sent, in the exact scene." },
      { t: "QR codes",               d: "Print, exhibit and hand out — buyers scan straight to the unit." },
      { t: "Payment plans",          d: "Show down payment, monthly and completion terms clearly." },
    ],
  },
  leads: {
    eyebrow: "Leads & follow-up",
    title: "Turn interest into qualified leads.",
    sub: "Every enquiry is linked to the unit, plan, PDF, shortlist and payment plan the buyer interacted with. Your team knows what the buyer wants — before making the follow-up call.",
    footnote: "CRM-lite lead tracking, WhatsApp-ready sharing, and easy hand-off to your CRM of choice.",
    items: [
      "Capture leads from unit pages",
      "Attach leads to the exact unit(s)",
      "Track buyer journey & shortlist",
      "WhatsApp-ready sharing",
      "Notes and follow-up statuses",
    ],
  },
  analytics: {
    eyebrow: "Analytics",
    title: "Know which units buyers really want.",
    sub: "Designed to shorten the sales cycle and improve buyer follow-up. See views, shortlists, comparisons, PDF downloads, WhatsApp clicks and lead conversions.",
    kpis: [
      { k: "Most-viewed",     l: "Units and blocks" },
      { k: "Shortlist rate",  l: "Interest signal per unit" },
      { k: "Lead conversion", l: "View → enquiry" },
      { k: "Reach",           l: "PDF · WhatsApp · Direct" },
    ],
  },
  showroom: {
    eyebrow: "Showroom experience",
    title: "Made for sales offices, iPads and investor meetings.",
    sub: "Present your project beautifully on tablets, showroom screens, desktop and mobile. Create a guided presentation experience that helps sales teams tell the project story with confidence.",
    tags: ["Sales offices", "iPad-first", "Property fairs", "Investor meetings", "Showroom mode"],
  },
  pricing: {
    eyebrow: "Pricing",
    title: "Straightforward plans that scale with your project.",
    perProject: "per project",
    setup: "Setup",
    month: "/month",
    startingAt: "Starting from",
    cta: "Choose",
    ctaCustom: "Talk to sales",
    disclaimer1: "Prices do not include the production of architectural drawings, apartment floor plans, 3D models, renderings, cinematic videos, 360 imagery, or custom visual content. These services can be quoted separately if required.",
    disclaimer2: "Monthly subscription covers platform access, hosting, updates and standard maintenance. Custom integrations, dedicated infrastructure and high-volume media hosting may require a separate quote.",
    plans: [
      { id: "starter",   name: "Starter",   for: "For projects up to 20 units",  setup: 7000,  month: 70,
        features: ["Interactive project page", "Up to 20 units", "Unit availability & status", "Unit detail pages", "Basic unit filtering", "Media library", "Shortlist & Compare", "Branded PDF offer", "Payment plan display", "Lead form", "Basic analytics", "QR / unit links", "Client portal", "1 language included"],
      },
      { id: "growth", featured: true, name: "Growth", for: "For projects up to 100 units", setup: 15000, month: 120,
        features: ["Everything in Starter", "Up to 100 units", "Etap / block / floor structure", "Smart Unit Finder", "Advanced filters", "Floor / Stack view", "360 tour support", "Presentation mode", "Multi-language support", "Enhanced analytics", "Showroom-ready experience"],
      },
      { id: "scale",  name: "Scale",   for: "For projects up to 500 units", setup: 25000, month: 200,
        features: ["Everything in Growth", "Up to 500 units", "Advanced project hierarchy", "Multiple presentation scenarios", "Advanced media library", "Extended analytics", "Sales team workflows", "Reservation hold (CRM-lite)", "Priority support", "Advanced showroom preparation", "Custom PDF / branding"],
      },
      { id: "enterprise", name: "Enterprise", for: "For projects with 500+ units or custom requirements", setup: null, month: null, custom: "Custom quote",
        features: ["Custom integrations", "CRM / ERP integration", "Online booking workflow", "Custom domain / white-label", "Dedicated hosting", "Advanced permissions", "3D model integration", "API access", "Custom reporting", "Multi-project developer portfolio"],
      },
    ],
  },
  finalCta: {
    title: "Ready to turn your project into an interactive sales experience?",
    sub:   "Book a personal walkthrough of the platform with a specialist. Or open the live demo and explore it yourself.",
    primary: "Book a demo", secondary: "Contact sales",
  },
  bookForm: {
    title: "Book a demo",
    sub:   "Tell us a little about your project. A specialist will follow up within one business day.",
    name: "Full name", email: "Work email", company: "Company", phone: "Phone (optional)",
    units: "Project size", unitsOpts: [
      { v: "up_to_20",  l: "Up to 20 units" },
      { v: "up_to_100", l: "Up to 100 units" },
      { v: "up_to_500", l: "Up to 500 units" },
      { v: "500_plus",  l: "500+ units" },
    ],
    plan: "Plan of interest", planOpts: [
      { v: "starter", l: "Starter" },
      { v: "growth",  l: "Growth" },
      { v: "scale",   l: "Scale" },
      { v: "enterprise", l: "Enterprise" },
      { v: "not_sure",   l: "Not sure yet" },
    ],
    message: "Anything we should know? (optional)",
    submit: "Request demo", cancel: "Cancel",
    thanks: "Thank you.",
    thanksSub: "A specialist will reach out within one business day.",
    close: "Close",
    error: "Something went wrong. Please try again or email hello@projevista.com.",
  },
  footer: {
    brand: "Projevista",
    tagline: "Interactive sales experiences for real estate developers.",
    contact: "Contact", privacy: "Privacy", terms: "Terms",
    copyright: (year) => `© ${year} Projevista. All rights reserved.`,
  },
};

const ar = {
  nav:  { explore: "استعراض تجريبي", signIn: "تسجيل الدخول", bookDemo: "احجز عرضاً" },
  hero: {
    eyebrow: "منصة بيع عقاري تفاعلية",
    headline: "حوّل مشروعك العقاري إلى تجربة بيع تفاعلية فاخرة.",
    sub: "اسمح للمشترين باستكشاف المشروع، اختيار الوحدات المتاحة، مقارنة الشقق، مشاهدة المخططات والجولات 360، استلام عروض PDF مخصصة، والتواصل مع فريق المبيعات من خلال منصة واحدة مصممة للمطورين العقاريين.",
    primary: "احجز عرضاً تجريبياً", secondary: "شاهد تجربة تفاعلية",
    ipad: "جاهزة لصالات البيع · آيباد · حاسوب · جوّال",
  },
  problem: {
    eyebrow: "لماذا يهم",
    title: "المواقع التقليدية تعرض المعلومات فقط. أما التجربة التفاعلية فتساعد العميل على اتخاذ القرار.",
    sub: "المشتري لا يريد تصفح كتالوجات طويلة. يريد أن يفهم المشروع، يقارن الوحدات المناسبة، يرى الأسعار والتوفر، يراجع خطط الدفع، ثم يتواصل مع فريق المبيعات وهو أكثر جاهزية.",
    cards: [
      { t: "الكتيبات تصبح قديمة بسرعة", d: "الأسعار والتوفر يتغيران بمجرد طباعة أي ملف PDF." },
      { t: "فريق المبيعات يكرر نفسه",     d: "أسئلة السعر والتوفر والإطلالة تستهلك ساعات كل أسبوع." },
      { t: "العميل لا يستطيع المقارنة",   d: "بدون واجهة ذكية، اختيار بين ٤٠ شقة يصبح مربكاً." },
      { t: "العملاء المحتملون يضيعون",    d: "المتابعة العامة تفقد الفرصة عندما لا ترتبط بما شاهده العميل." },
    ],
  },
  immersive: {
    eyebrow: "تجربة غامرة",
    title: "اجعل العميل يشعر بالمشروع قبل زيارته.",
    sub: "اصطحب العميل داخل المشروع من المنظور العام إلى البلوكات والطوابق والشقق. اعرض تفاصيل الوحدة، المخطط، الإطلالة، الصور، والجولات 360 ضمن تجربة سلسة مناسبة للآيباد وشاشات صالات البيع.",
    bullets: ["تصفح المخطط العام", "تنقل بين البلوكات والطوابق", "توفر مباشر", "جولات 360 ومعارض صور", "خطط الدفع", "تواصل بلمسة واحدة"],
  },
  smart: {
    eyebrow: "اختيار ذكي للوحدات",
    title: "ساعد العميل على إيجاد الوحدة المناسبة بسرعة.",
    sub: "فلترة حسب نوع الشقة، السعر، المساحة، الإطلالة، الطابق، الحالة، وأي حقول خاصة بالمشروع. يمكن لفريق المبيعات تضييق مئات الوحدات إلى قائمة قصيرة خلال ثوانٍ.",
    chips: ["1+1 / 2+1 / 3+1", "إطلالة بحرية", "أقل من ١٥٠٬٠٠٠ دولار", "٧٠–١١٠ م²", "المتاحة فقط", "الطابق ٥ فأعلى"],
  },
  sales: {
    eyebrow: "أدوات المبيعات",
    title: "مصممة لفرق مبيعات العقارات.",
    sub: "كل ما يحتاجه فريقك للانتقال من التصفح إلى العرض الموقّع — داخل تجربة واحدة.",
    items: [
      { t: "قائمة مختصرة",         d: "احفظ المفضلات أثناء زيارة صالة العرض أو العرض للعميل." },
      { t: "مقارنة الشقق",           d: "مقارنة جنباً إلى جنب للمخطط والإطلالة والسعر والمساحة." },
      { t: "عروض PDF بعلامة تجارية", d: "اصنع عرضاً فاخراً بعلامة المطور بلمسة واحدة." },
      { t: "روابط مباشرة للوحدات",   d: "العميل يفتح تماماً الشقة والمشهد الذي أرسلته." },
      { t: "رموز QR",                d: "اطبعها في المعارض واليافطات — يمسحها العميل ليصل مباشرة إلى الوحدة." },
      { t: "خطط الدفع",              d: "اعرض الدفعة الأولى والقسط الشهري وشروط التسليم بوضوح." },
    ],
  },
  leads: {
    eyebrow: "العملاء والمتابعة",
    title: "حوّل الاهتمام إلى عملاء محتملين مؤهلين.",
    sub: "كل طلب تواصل يمكن ربطه بالوحدة التي شاهدها العميل، والمخطط، والعرض، وخطة الدفع، والوحدات التي قارنها أو أضافها للقائمة المختصرة. هكذا يعرف فريق المبيعات ما يريده العميل قبل الاتصال.",
    footnote: "تتبع مبسّط للعملاء المحتملين، مشاركة واتساب جاهزة، وتسليم سهل إلى أي نظام CRM.",
    items: [
      "التقاط طلبات من صفحات الوحدات",
      "ربط العملاء بالوحدات المهتم بها",
      "تتبع رحلة المشتري والقائمة المختصرة",
      "مشاركة واتساب جاهزة",
      "ملاحظات وحالات المتابعة",
    ],
  },
  analytics: {
    eyebrow: "التحليلات",
    title: "اعرف أي الوحدات تجذب المشترين أكثر.",
    sub: "مصممة لتقصير دورة البيع وتحسين المتابعة. تابع المشاهدات، القوائم المختصرة، المقارنات، تحميلات PDF، نقرات واتساب، وتحويلات العملاء المحتملين.",
    kpis: [
      { k: "الأكثر مشاهدة",   l: "الوحدات والبلوكات" },
      { k: "نسبة القائمة",   l: "مؤشر الاهتمام لكل وحدة" },
      { k: "معدل التحويل",   l: "مشاهدة إلى استفسار" },
      { k: "الوصول",         l: "PDF · واتساب · روابط مباشرة" },
    ],
  },
  showroom: {
    eyebrow: "تجربة صالة البيع",
    title: "مصممة لصالات البيع، الآيباد، واجتماعات المستثمرين.",
    sub: "اعرض مشروعك بطريقة مبهرة على الآيباد، الشاشات، الكمبيوتر، والموبايل. أنشئ تجربة عرض موجهة تساعد فريق المبيعات على شرح المشروع بثقة.",
    tags: ["صالات البيع", "الآيباد أولاً", "معارض العقارات", "لقاءات المستثمرين", "وضع صالة العرض"],
  },
  pricing: {
    eyebrow: "الأسعار",
    title: "خطط واضحة تنمو مع مشروعك.",
    perProject: "لكل مشروع",
    setup: "الإعداد",
    month: "/شهرياً",
    startingAt: "يبدأ من",
    cta: "اختر",
    ctaCustom: "تواصل مع المبيعات",
    disclaimer1: "الأسعار لا تشمل تجهيز المخططات المعمارية، مخططات الشقق، المجسمات ثلاثية الأبعاد، الرندرات، الفيديوهات السينمائية، صور 360، أو أي محتوى بصري مخصص. يمكن تسعير هذه الخدمات بشكل منفصل عند الحاجة.",
    disclaimer2: "الاشتراك الشهري يشمل الوصول إلى المنصة والاستضافة والتحديثات والصيانة القياسية. أي تكاملات مخصصة أو بنية تحتية مخصصة أو استضافة وسائط عالية الحجم قد تتطلب عرضاً منفصلاً.",
    plans: [
      { id: "starter",   name: "المبتدئة", for: "لمشاريع حتى ٢٠ وحدة", setup: 7000, month: 70,
        features: ["صفحة مشروع تفاعلية", "حتى ٢٠ وحدة", "إدارة توفر الوحدات", "صفحات تفاصيل الوحدات", "فلترة أساسية", "مكتبة وسائط", "قائمة مختصرة ومقارنة", "عرض PDF بعلامة تجارية", "عرض خطة الدفع", "نموذج عملاء", "تحليلات أساسية", "روابط QR", "بوابة العميل", "لغة واحدة مضمّنة"],
      },
      { id: "growth",  featured: true, name: "النمو", for: "لمشاريع حتى ١٠٠ وحدة", setup: 15000, month: 120,
        features: ["كل ما في المبتدئة", "حتى ١٠٠ وحدة", "هيكل مراحل / بلوكات / طوابق", "الباحث الذكي عن الوحدات", "فلترة متقدمة", "عرض الطوابق والوحدات", "دعم جولات 360", "وضع العرض التقديمي", "دعم متعدد اللغات", "تحليلات موسعة", "تجربة صالة عرض جاهزة"],
      },
      { id: "scale",   name: "التوسع", for: "لمشاريع حتى ٥٠٠ وحدة", setup: 25000, month: 200,
        features: ["كل ما في النمو", "حتى ٥٠٠ وحدة", "هيكل مشروع متقدم", "سيناريوهات عرض متعددة", "مكتبة وسائط متقدمة", "تحليلات موسعة", "سير عمل لفرق البيع", "احتجاز وحدات (CRM مبسّط)", "دعم بأولوية", "تجهيز متقدم لصالات العرض", "PDF وعلامة تجارية مخصصة"],
      },
      { id: "enterprise", name: "المؤسسات", for: "لمشاريع أكبر من ٥٠٠ وحدة أو متطلبات مخصصة", setup: null, month: null, custom: "عرض مخصص",
        features: ["تكاملات مخصصة", "تكامل CRM / ERP", "سير عمل الحجز الإلكتروني", "دومين مخصص / علامة بيضاء", "استضافة مخصصة", "صلاحيات متقدمة", "دمج نماذج ثلاثية الأبعاد", "وصول API", "تقارير مخصصة", "بوابة مطور متعددة المشاريع"],
      },
    ],
  },
  finalCta: {
    title: "هل أنت جاهز لتحويل مشروعك إلى تجربة بيع تفاعلية؟",
    sub:   "احجز جولة شخصية داخل المنصة مع أحد المختصين، أو افتح التجربة التفاعلية المباشرة الآن.",
    primary: "احجز عرضاً", secondary: "تواصل مع المبيعات",
  },
  bookForm: {
    title: "احجز عرضاً تجريبياً",
    sub:   "أخبرنا قليلاً عن مشروعك. سيتواصل معك مختص خلال يوم عمل واحد.",
    name: "الاسم الكامل", email: "البريد الإلكتروني", company: "الشركة", phone: "الهاتف (اختياري)",
    units: "حجم المشروع", unitsOpts: [
      { v: "up_to_20",  l: "حتى ٢٠ وحدة" },
      { v: "up_to_100", l: "حتى ١٠٠ وحدة" },
      { v: "up_to_500", l: "حتى ٥٠٠ وحدة" },
      { v: "500_plus",  l: "أكثر من ٥٠٠ وحدة" },
    ],
    plan: "الخطة التي تهمك", planOpts: [
      { v: "starter", l: "المبتدئة" },
      { v: "growth",  l: "النمو" },
      { v: "scale",   l: "التوسع" },
      { v: "enterprise", l: "المؤسسات" },
      { v: "not_sure",   l: "لست متأكداً" },
    ],
    message: "أي معلومات إضافية؟ (اختياري)",
    submit: "طلب عرض", cancel: "إلغاء",
    thanks: "شكراً لك.",
    thanksSub: "سيتواصل معك مختص خلال يوم عمل واحد.",
    close: "إغلاق",
    error: "حدث خطأ ما. الرجاء المحاولة مرة أخرى أو مراسلتنا على hello@projevista.com.",
  },
  footer: {
    brand: "بروجيفيستا",
    tagline: "تجارب بيع تفاعلية للمطورين العقاريين.",
    contact: "تواصل", privacy: "الخصوصية", terms: "الشروط",
    copyright: (year) => `© ${year} بروجيفيستا. جميع الحقوق محفوظة.`,
  },
};

const trTR = {
  nav:  { explore: "İnteraktif demo", signIn: "Giriş yap", bookDemo: "Demo talep et" },
  hero: {
    eyebrow: "İnteraktif gayrimenkul satış platformu",
    headline: "Gayrimenkul projenizi etkileşimli bir satış deneyimine dönüştürün.",
    sub: "Alıcılar projenizi keşfetsin, uygun daireleri bulsun, seçenekleri karşılaştırsın, planları ve 360 turları incelesin, markalı teklif PDF’i alsın ve satış ekibinizle tek bir premium platform üzerinden iletişime geçsin.",
    primary: "Demo talep et", secondary: "Canlı demoyu görüntüle",
    ipad: "Showroom hazır · iPad · Masaüstü · Mobil",
  },
  problem: {
    eyebrow: "Neden önemli",
    title: "Klasik siteler yalnızca bilgi gösterir. Etkileşimli deneyimler ise karar verdirir.",
    sub: "Alıcılar uzun broşürlerde kaybolmak istemez. Projeyi anlamak, doğru daireleri karşılaştırmak, müsaitlik ve fiyatları görmek, ödeme planlarını incelemek ve satış ekibinizle daha bilinçli şekilde iletişime geçmek ister.",
    cards: [
      { t: "Broşürler haftalar içinde eskir",   d: "Fiyatlar ve stok, PDF basıldığı anda güncelliğini kaybeder." },
      { t: "Satış ekibi kendini tekrar eder",   d: "Aynı müsaitlik, fiyat ve manzara soruları haftada saatler alıyor." },
      { t: "Alıcı karşılaştırma yapamıyor",     d: "Akıllı arayüz olmadan 40 daire arasında seçim bunaltıcı geliyor." },
      { t: "Sıcak lead’ler kaybolur",           d: "Genel takip, alıcının incelediği detayları göremediğinde fırsatı kaçırır." },
    ],
  },
  immersive: {
    eyebrow: "Kapsayıcı deneyim",
    title: "Alıcıya projeyi ziyaret etmeden önce hissettirin.",
    sub: "Projeyi genel görünümden bloklara, katlara ve dairelere kadar akıcı bir deneyimle sunun. Daire detayları, planlar, manzaralar, galeriler ve 360 turlar showroom ve iPad sunumlarına uygun şekilde gösterilir.",
    bullets: ["Ana plan gezintisi", "Blok ve kat ayrıntısı", "Canlı müsaitlik", "360 tur ve galeri", "Ödeme planları", "Tek dokunuşla iletişim"],
  },
  smart: {
    eyebrow: "Akıllı daire seçimi",
    title: "Alıcıların doğru daireyi daha hızlı bulmasını sağlayın.",
    sub: "Daire tipi, fiyat aralığı, alan, manzara, kat, durum ve projeye özel alanlara göre filtreleme yapın. Yüzlerce daire, saniyeler içinde odaklı bir kısa listeye dönüşür.",
    chips: ["1+1 / 2+1 / 3+1", "Deniz manzarası", "150.000 $ altında", "70–110 m²", "Sadece müsait", "5. kat üstü"],
  },
  sales: {
    eyebrow: "Satış araçları",
    title: "Gayrimenkul satış ekipleri için tasarlandı.",
    sub: "Göz atmadan imzalı teklife kadar ekibinizin ihtiyacı olan her şey — tek deneyim içinde.",
    items: [
      { t: "Daireleri kısa listeye ekle", d: "Showroom ziyareti veya sunum sırasında favorileri kaydedin." },
      { t: "Karşılaştırma",                d: "Plan, manzara, fiyat ve alanı yan yana karşılaştırın." },
      { t: "Markalı PDF teklif",           d: "Tek dokunuşla premium, geliştiriciye özel bir teklif oluşturun." },
      { t: "Doğrudan daire linki",         d: "Alıcı gönderdiğiniz daireyi ve sahneyi tam olarak açar." },
      { t: "QR kodlar",                    d: "Fuar ve broşürlerde kullanın — alıcı doğrudan daireye ulaşır." },
      { t: "Ödeme planları",                d: "Peşinat, taksit ve tamamlama koşullarını net gösterin." },
    ],
  },
  leads: {
    eyebrow: "Lead ve takip",
    title: "İlgiyi nitelikli potansiyel müşteriye dönüştürün.",
    sub: "Her talep; alıcının incelediği daire, plan, PDF, ödeme planı, kısa liste ve karşılaştırmalarla ilişkilendirilebilir. Satış ekibiniz, alıcının ne istediğini takip görüşmesinden önce bilir.",
    footnote: "CRM-lite lead takibi, WhatsApp’a hazır paylaşım ve dilediğiniz CRM’e kolay aktarım.",
    items: [
      "Daire sayfalarından lead yakalama",
      "Lead’i tam olarak ilgili daireye bağlama",
      "Alıcı yolculuğunu ve kısa listeyi takip",
      "WhatsApp’a hazır paylaşım",
      "Notlar ve takip durumları",
    ],
  },
  analytics: {
    eyebrow: "Analitik",
    title: "Alıcıların gerçekten hangi daireleri istediğini görün.",
    sub: "Satış döngüsünü kısaltmak ve takip kalitesini artırmak için tasarlandı. Görüntüleme, kısa liste, karşılaştırma, PDF, WhatsApp tıklamaları ve lead dönüşümlerini takip edin.",
    kpis: [
      { k: "En çok görülen",     l: "Daireler ve bloklar" },
      { k: "Kısa liste oranı",   l: "Daire başına ilgi sinyali" },
      { k: "Dönüşüm",             l: "Görüntüleme → talep" },
      { k: "Erişim",              l: "PDF · WhatsApp · Direct" },
    ],
  },
  showroom: {
    eyebrow: "Showroom deneyimi",
    title: "Satış ofisleri, iPad sunumları ve yatırımcı toplantıları için tasarlandı.",
    sub: "Projenizi tabletlerde, showroom ekranlarında, masaüstünde ve mobilde güçlü bir şekilde sunun. Satış ekibinizin projeyi güvenle anlatmasını sağlayan yönlendirilmiş bir sunum deneyimi oluşturun.",
    tags: ["Satış ofisleri", "Öncelikli iPad", "Emlak fuarları", "Yatırımcı görüşmeleri", "Showroom modu"],
  },
  pricing: {
    eyebrow: "Fiyatlandırma",
    title: "Projenizle birlikte büyüyen net planlar.",
    perProject: "proje başına",
    setup: "Kurulum",
    month: "/ay",
    startingAt: "Başlangıç",
    cta: "Seç",
    ctaCustom: "Satış ile görüş",
    disclaimer1: "Fiyatlara mimari çizimler, daire planları, 3D modeller, render görselleri, sinematik videolar, 360 görseller veya özel görsel içerik üretimi dahil değildir. Bu hizmetler ihtiyaç halinde ayrıca fiyatlandırılır.",
    disclaimer2: "Aylık abonelik; platform erişimi, barındırma, güncellemeler ve standart bakımı kapsar. Özel entegrasyonlar, ayrılmış altyapı ve yüksek hacimli medya barındırma ayrı fiyatlandırılabilir.",
    plans: [
      { id: "starter",   name: "Başlangıç", for: "20 daireye kadar projeler", setup: 7000, month: 70,
        features: ["İnteraktif proje sayfası", "20 daireye kadar", "Müsaitlik / durum yönetimi", "Daire detay sayfaları", "Temel filtreler", "Medya kütüphanesi", "Kısa liste ve Karşılaştırma", "Markalı PDF teklif", "Ödeme planı gösterimi", "Lead formu", "Temel analitik", "QR / daire linki", "Müşteri portalı", "1 dil dahildir"],
      },
      { id: "growth",  featured: true, name: "Büyüme", for: "100 daireye kadar projeler", setup: 15000, month: 120,
        features: ["Başlangıç paketindeki her şey", "100 daireye kadar", "Etap / blok / kat yapısı", "Akıllı Daire Bulucu", "Gelişmiş filtreler", "Kat / Stack görünümü", "360 tur desteği", "Sunum modu", "Çoklu dil desteği", "Gelişmiş analitik", "Showroom-hazır deneyim"],
      },
      { id: "scale",   name: "Ölçek", for: "500 daireye kadar projeler", setup: 25000, month: 200,
        features: ["Büyüme paketindeki her şey", "500 daireye kadar", "Gelişmiş proje hiyerarşisi", "Çoklu sunum senaryoları", "Gelişmiş medya kütüphanesi", "Genişletilmiş analitik", "Satış ekibi iş akışları", "Rezervasyon tutma (CRM-lite)", "Öncelikli destek", "Gelişmiş showroom hazırlığı", "Özel PDF / marka"],
      },
      { id: "enterprise", name: "Kurumsal", for: "500+ daire veya özel gereksinimler", setup: null, month: null, custom: "Özel teklif",
        features: ["Özel entegrasyonlar", "CRM / ERP entegrasyonu", "Online rezervasyon", "Özel alan / white-label", "Adanmış barındırma", "Gelişmiş yetki yönetimi", "3D model entegrasyonu", "API erişimi", "Özel raporlama", "Çoklu proje geliştirici portalı"],
      },
    ],
  },
  finalCta: {
    title: "Projenizi etkileşimli bir satış deneyimine dönüştürmeye hazır mısınız?",
    sub:   "Uzmanla birlikte platformun kişisel turunu talep edin veya canlı demoyu kendiniz keşfedin.",
    primary: "Demo talep et", secondary: "Satış ile görüş",
  },
  bookForm: {
    title: "Demo talep et",
    sub:   "Projeniz hakkında biraz bilgi paylaşın. Bir uzman size bir iş günü içinde ulaşacaktır.",
    name: "Ad Soyad", email: "İş e-postası", company: "Şirket", phone: "Telefon (opsiyonel)",
    units: "Proje büyüklüğü", unitsOpts: [
      { v: "up_to_20",  l: "20 daireye kadar" },
      { v: "up_to_100", l: "100 daireye kadar" },
      { v: "up_to_500", l: "500 daireye kadar" },
      { v: "500_plus",  l: "500+ daire" },
    ],
    plan: "İlgilendiğiniz plan", planOpts: [
      { v: "starter", l: "Başlangıç" },
      { v: "growth",  l: "Büyüme" },
      { v: "scale",   l: "Ölçek" },
      { v: "enterprise", l: "Kurumsal" },
      { v: "not_sure",   l: "Henüz emin değilim" },
    ],
    message: "Belirtmek istediğiniz bir şey var mı? (opsiyonel)",
    submit: "Demo iste", cancel: "İptal",
    thanks: "Teşekkürler.",
    thanksSub: "Bir uzman bir iş günü içinde size ulaşacaktır.",
    close: "Kapat",
    error: "Bir şeyler ters gitti. Lütfen tekrar deneyin veya hello@projevista.com adresine yazın.",
  },
  footer: {
    brand: "Projevista",
    tagline: "Gayrimenkul geliştiricileri için etkileşimli satış deneyimleri.",
    contact: "İletişim", privacy: "Gizlilik", terms: "Şartlar",
    copyright: (year) => `© ${year} Projevista. Tüm hakları saklıdır.`,
  },
};

export const LANDING_I18N = { en, ar, tr: trTR };

const LANDING_EXTRA = {
  "en": {
    "title": "Projevista — Interactive real estate sales",
    "available": "Available",
    "reserved": "Reserved",
    "sea_view": "Sea view",
    "floor": "Floor",
    "matches": "12 matches",
    "shortlisted": "3 shortlisted",
    "payment_plan": "Payment plan",
    "payment_example": "30% down · 48 months",
    "pdf_offer": "PDF offer",
    "floor_plan_3d": "3D floor plan",
    "finder": "Smart unit finder",
    "results": "12 results",
    "price": "Price",
    "area": "Area",
    "view": "View",
    "sea": "Sea",
    "garden": "Garden",
    "city": "City",
    "type": "Type",
    "status": "Status",
    "new_enquiry": "New enquiry",
    "journey": "3 shortlisted units · 2 compared · 1 PDF sent",
    "name": "Name",
    "phone": "Phone",
    "interest": "Interest",
    "language": "Language",
    "english": "English",
    "whatsapp_ready": "WhatsApp ready",
    "call_now": "Call now",
    "showroom": "Showroom",
    "fullscreen": "Full-screen",
    "landscape": "Landscape",
    "investor": "Investor",
    "presentation": "Presentation",
    "guided": "Guided",
    "scenarios": "Scenarios",
    "recommended": "Recommended"
  },
  "ar": {
    "title": "بروجيڤيستا — تجارب بيع عقاري تفاعلية",
    "available": "متاحة",
    "reserved": "محجوزة",
    "sea_view": "إطلالة بحرية",
    "floor": "الطابق",
    "matches": "12 نتيجة",
    "shortlisted": "3 في المفضلة",
    "payment_plan": "خطة السداد",
    "payment_example": "دفعة مقدمة 30% · 48 شهرًا",
    "pdf_offer": "عرض PDF",
    "floor_plan_3d": "مخطط ثلاثي الأبعاد",
    "finder": "البحث الذكي عن الوحدات",
    "results": "12 نتيجة",
    "price": "السعر",
    "area": "المساحة",
    "view": "الإطلالة",
    "sea": "البحر",
    "garden": "الحديقة",
    "city": "المدينة",
    "type": "النوع",
    "status": "الحالة",
    "new_enquiry": "استفسار جديد",
    "journey": "3 وحدات في المفضلة · وحدتان للمقارنة · عرض PDF مرسل",
    "name": "الاسم",
    "phone": "الهاتف",
    "interest": "الاهتمام",
    "language": "اللغة",
    "english": "الإنجليزية",
    "whatsapp_ready": "جاهز لواتساب",
    "call_now": "اتصل الآن",
    "showroom": "صالة العرض",
    "fullscreen": "ملء الشاشة",
    "landscape": "وضع أفقي",
    "investor": "المستثمر",
    "presentation": "عرض تقديمي",
    "guided": "موجّه",
    "scenarios": "سيناريوهات",
    "recommended": "موصى به"
  },
  "tr": {
    "title": "Projevista — Etkileşimli gayrimenkul satışı",
    "available": "Müsait",
    "reserved": "Rezerve",
    "sea_view": "Deniz manzarası",
    "floor": "Kat",
    "matches": "12 sonuç",
    "shortlisted": "3 daire listede",
    "payment_plan": "Ödeme planı",
    "payment_example": "%30 peşinat · 48 ay",
    "pdf_offer": "PDF teklif",
    "floor_plan_3d": "3B kat planı",
    "finder": "Akıllı daire bulucu",
    "results": "12 sonuç",
    "price": "Fiyat",
    "area": "Alan",
    "view": "Manzara",
    "sea": "Deniz",
    "garden": "Bahçe",
    "city": "Şehir",
    "type": "Tip",
    "status": "Durum",
    "new_enquiry": "Yeni başvuru",
    "journey": "3 daire listede · 2 karşılaştırıldı · 1 PDF gönderildi",
    "name": "Ad",
    "phone": "Telefon",
    "interest": "İlgi",
    "language": "Dil",
    "english": "İngilizce",
    "whatsapp_ready": "WhatsApp hazır",
    "call_now": "Şimdi ara",
    "showroom": "Showroom",
    "fullscreen": "Tam ekran",
    "landscape": "Yatay",
    "investor": "Yatırımcı",
    "presentation": "Sunum",
    "guided": "Rehberli",
    "scenarios": "Senaryolar",
    "recommended": "Önerilen"
  }
};
for (const lang of Object.keys(LANDING_I18N)) LANDING_I18N[lang].ui = LANDING_EXTRA[lang];

export const LOGIN_I18N = {
  "en": {
    "title": "Sign in · Projevista",
    "studio": "Studio access",
    "intro": "Build cinematic interactive sales experiences.",
    "sign_in": "Sign in",
    "welcome": "Welcome back",
    "email": "Email",
    "password": "Password",
    "failed": "Sign-in failed. Check your email and password and try again.",
    "language": "Language"
  },
  "ar": {
    "title": "تسجيل الدخول · Projevista",
    "studio": "الدخول إلى الاستوديو",
    "intro": "أنشئ تجارب مبيعات تفاعلية سينمائية.",
    "sign_in": "تسجيل الدخول",
    "welcome": "مرحبًا بعودتك",
    "email": "البريد الإلكتروني",
    "password": "كلمة المرور",
    "failed": "تعذّر تسجيل الدخول. تحقق من البريد وكلمة المرور وحاول مجددًا.",
    "language": "اللغة"
  },
  "tr": {
    "title": "Giriş yap · Projevista",
    "studio": "Stüdyo erişimi",
    "intro": "Sinematik, etkileşimli satış deneyimleri oluşturun.",
    "sign_in": "Giriş yap",
    "welcome": "Tekrar hoş geldiniz",
    "email": "E-posta",
    "password": "Şifre",
    "failed": "Giriş yapılamadı. E-posta adresinizi ve şifrenizi kontrol edip tekrar deneyin.",
    "language": "Dil"
  },
  "ru": {
    "title": "Вход · Projevista",
    "studio": "Доступ в студию",
    "intro": "Создавайте интерактивные презентации для продажи недвижимости.",
    "sign_in": "Войти",
    "welcome": "С возвращением",
    "email": "Электронная почта",
    "password": "Пароль",
    "failed": "Не удалось войти. Проверьте адрес почты и пароль и повторите попытку.",
    "language": "Язык"
  }
};
